"use client";

import Toast from "@/components/atoms/block/Toast";
import ToastWrapper from "@/components/atoms/block/ToastWrapper";
import { nanoid } from "nanoid";
import { useCallback, useMemo, useState } from "react";
import ToastContext from "./helpers/ToastContext";
import type INotification from "./types/INotification";
import type NotificationType from "./types/NotificationType";

interface IToastProviderProps {
  children: React.ReactNode;
}

export function ToastProvider({ children }: IToastProviderProps) {
  const [notifications, setNotifications] = useState<INotification[]>([]);

  const removeNotificationById = useCallback((id: string) => {
    setNotifications((previousNotifications) => {
      return previousNotifications.filter((notification) => notification.id !== id);
    });
  }, []);

  const addNotification = useCallback(
    (type: NotificationType, message: string) => {
      const id = nanoid();
      setNotifications((previousNotifications) => {
        const newNotification: INotification = { id, type, message };

        return [...previousNotifications, newNotification];
      });

      setTimeout(() => {
        removeNotificationById(id);
      }, 5000);
    },
    [removeNotificationById],
  );

  const value = useMemo(
    () => ({
      values: {
        notifications,
      },
      actions: {
        addNotification,
        removeNotificationById,
      },
    }),
    [notifications, addNotification, removeNotificationById],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastWrapper>
        {notifications.map((notification) => (
          <Toast
            key={notification.id}
            theme={notification.type}
            onClose={() => removeNotificationById(notification.id)}
          >
            {notification.message}
          </Toast>
        ))}
      </ToastWrapper>
    </ToastContext.Provider>
  );
}
